import { type List } from "immutable";
import React from "react";
import { type ConnectedProps, connect } from "react-redux";

import { Spinner } from "@performyard/py-components";

import Button from "../../../../components/Button";
import EmployeePicker from "../../../../components/EmployeePicker/compatibility";

import Curtain from "../../../../containers/PageCurtain";

import { addMemberToQueue, closeCurtain, removeMemberFromQueue, sendNewAdmins, sendNewMembers } from "../../actions";

import { getIsRequesting } from "../../../../selectors/requests";
import { getPageUISelector, getQueuedMemberList } from "../../selectors";

interface AddMembersCurtainProps extends PropsFromRedux {
	curtainName: string | null;
	isRequesting: boolean;
	memberQueue: List<any>;
}

const AddMembersCurtain: React.FC<AddMembersCurtainProps> = ({
	curtainName,
	isRequesting,
	memberQueue,
	closeCurtain,
	addMemberToQueue,
	removeMemberFromQueue,
	sendNewAdmins,
	sendNewMembers,
}) => {
	const handleClose = () => {
		closeCurtain();
	};

	const handleEmployeeChange = (member: any) => {
		addMemberToQueue(member);
	};

	const handleRemoveFromQueue = (employeeId: string) => {
		removeMemberFromQueue(employeeId);
	};

	const handleAddUsers = () => {
		if (curtainName === "addAdmins") {
			sendNewAdmins();
		} else {
			sendNewMembers();
		}
	};

	if (["addMembers", "addAdmins"].indexOf(curtainName ?? "") < 0) {
		return <div />;
	}

	const employeeType = curtainName === "addMembers" ? "Member" : "Admin";

	return (
		<Curtain color="black" onClose={handleClose}>
			<div className="group-add-members-curtain" data-testid="group-add-curtain">
				<EmployeePicker
					value={memberQueue}
					onChange={handleEmployeeChange}
					label="Employee Name"
					isMultiSelect={true}
					onDelete={handleRemoveFromQueue}
					version="searchService"
					searchServiceProps={{
						// We want to allow manual selection of admins, but general group
						// member adding will be locked if the Group field is managed by HRIS.
						department_hris_sync_exclusion: curtainName === "addMembers",
					}}
				/>
				{memberQueue.size > 0 ? (
					<div className="button-container">
						{isRequesting ? (
							<Spinner size={20} />
						) : (
							<Button color="blue" onClick={handleAddUsers}>
								{memberQueue.size > 1 ? `Add ${employeeType}s` : `Add ${employeeType}`}
							</Button>
						)}
					</div>
				) : (
					<div />
				)}
			</div>
		</Curtain>
	);
};

const mapStateToProps = (state: any) => {
	const ui = getPageUISelector(state);
	const addAdminsRequesting = getIsRequesting(state, "GROUPS:ADMINS.ADD");
	const addMembersRequesting = getIsRequesting(state, "GROUPS:MEMBERS.ADD");

	return {
		memberQueue: getQueuedMemberList(state),
		curtainName: ui.get("openCurtain", null),
		isRequesting: addAdminsRequesting || addMembersRequesting,
	};
};

const mapDispatchToProps = {
	closeCurtain,
	sendNewAdmins,
	sendNewMembers,
	addMemberToQueue,
	removeMemberFromQueue,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(AddMembersCurtain);
